import React from 'react';
import { Badge, BadgeTone, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import {
  EnvironmentRuntime,
  EnvironmentRuntimeStatus,
  EnvironmentStatus,
} from '../../services/projectEnvironment';

type Meta = { label: string; tone: BadgeTone; spinning?: boolean };

const STATUS_META: Record<EnvironmentStatus, Meta> = {
  // Named for what the user should do next, not for what already happened —
  // a created environment has a URL that does not resolve until it is deployed.
  created: { label: 'Not deployed', tone: 'neutral' },
  deploying: { label: 'Deploying', tone: 'brand', spinning: true },
  deploy_failed: { label: 'Deploy failed', tone: 'error' },
  deployed: { label: 'Deployed', tone: 'success' },
};

/** Status pill — one per record status. */
export const StatusBadge: React.FC<{ status: EnvironmentStatus }> = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.deploy_failed;
  return (
    <Badge tone={meta.tone} spinning={meta.spinning}>
      {meta.label}
    </Badge>
  );
};

const RUNTIME_META: Record<EnvironmentRuntimeStatus, Meta | null> = {
  running: { label: 'Running', tone: 'success' },
  stopped: { label: 'Stopped', tone: 'neutral' },
  starting: { label: 'Starting', tone: 'brand', spinning: true },
  stopping: { label: 'Stopping', tone: 'brand', spinning: true },
  partial: { label: 'Partially running', tone: 'warning' },
  error: { label: 'Unhealthy', tone: 'error' },
  // Docker unreachable — a neutral nothing, not an error.
  unknown: null,
};

/** Live container-state pill — Running · Stopped · Starting · … Renders nothing for 'unknown'. */
export const RuntimeBadge: React.FC<{ status: EnvironmentRuntimeStatus }> = ({ status }) => {
  const meta = RUNTIME_META[status];
  if (!meta) return null;
  return (
    <Badge tone={meta.tone} spinning={meta.spinning}>
      {meta.label}
    </Badge>
  );
};

/** The one state an environment is in, resolved once.
 *
 * The record status is the default story — 'Deployed' already means built and
 * serving, so 'running' needs no label of its own. The runtime takes over only
 * when a *deployed* environment's containers deviate from that: stopped,
 * starting, stopping, partial or unhealthy.
 *
 * Before it is deployed the runtime is ignored entirely. Such an environment
 * reports 'stopped' because it has no containers, and showing 'Stopped' would
 * imply it could simply be started.
 *
 * Shared by the dot and the pill so the two can never describe different
 * things. */
export const environmentMeta = (
  status: EnvironmentStatus,
  runtime?: EnvironmentRuntime
): Meta => {
  if (
    status === 'deployed' &&
    runtime &&
    runtime.status !== 'running' &&
    RUNTIME_META[runtime.status]
  ) {
    return RUNTIME_META[runtime.status] as Meta;
  }
  return STATUS_META[status] || STATUS_META.deploy_failed;
};

const DOT: Record<BadgeTone, string> = {
  neutral: 'bg-utility-gray-300',
  brand: 'bg-utility-blue-500',
  success: 'bg-utility-success-500',
  warning: 'bg-utility-warning-500',
  error: 'bg-utility-error-500',
};

/** The status glyph carried by every row.
 *
 * Constant, so the eye can read the column at a glance; the pill beside it
 * appears only when there is something the dot cannot say. Titled with the
 * full label, so nothing the pill used to carry is actually lost. */
export const StatusDot: React.FC<{
  status: EnvironmentStatus;
  runtime?: EnvironmentRuntime;
}> = ({ status, runtime }) => {
  const meta = environmentMeta(status, runtime);
  return (
    <span
      title={meta.label}
      aria-label={meta.label}
      role="img"
      className={`w-[8px] h-[8px] rounded-bg-v2-full shrink-0 ${DOT[meta.tone]} ${
        meta.spinning ? 'animate-pulse' : ''
      }`}
    />
  );
};

/** The status node that sits on the pipeline connector.
 *
 * Larger than the inline dot and drawn *over* the line, so the chain reads as
 * a sequence of stations rather than a list with a stripe down the side. Solid
 * with a tick once the environment is deployed; hollow, on the card's own
 * background, until it is — which also masks the line behind it so the
 * connector appears to run between nodes rather than through them.
 */
/** How one state is drawn on the pipeline connector.
 *
 * Colour alone was not enough. Two different meanings shared the same white
 * dot — an environment that has never been deployed and one that is deployed
 * but stopped — and the second has containers, a database with data and a URL
 * that resolves the moment you start it. Saying "nothing here" about that is
 * wrong.
 *
 * So the rule is **filled means it exists, hollow means it does not**, and
 * every state also carries a glyph. That keeps the pipeline readable at 20px,
 * in greyscale, and to anyone who cannot separate the green from the amber.
 */
const NODE: Record<
  BadgeTone,
  { fill: string; glyph?: IconName }
> = {
  // Present and healthy.
  success: { fill: 'bg-utility-success-500', glyph: IconName.Check },
  // Present, idle. gray-400 rather than the dot's gray-300: a white glyph
  // needs something to sit on.
  neutral: { fill: 'bg-utility-gray-400', glyph: IconName.Stop },
  warning: { fill: 'bg-utility-warning-500', glyph: IconName.Alert },
  error: { fill: 'bg-utility-error-500', glyph: IconName.Alert },
  // Never drawn as a fill — see the in-progress branch below.
  brand: { fill: 'bg-utility-blue-500' },
};

/** The status node that sits on the pipeline connector.
 *
 * Larger than the inline dot and drawn *over* the line, so the chain reads as
 * a sequence of stations rather than a list with a stripe down the side. The
 * border matches the card so the connector appears to run between nodes rather
 * than through them.
 */
export const ChainNode: React.FC<{
  status: EnvironmentStatus;
  runtime?: EnvironmentRuntime;
}> = ({ status, runtime }) => {
  const meta = environmentMeta(status, runtime);
  const node = NODE[meta.tone];

  // Nothing exists yet — the one state that is genuinely empty, so it is the
  // one that gets an empty ring.
  const nothingYet = status === 'created';

  // No border colour here: each branch supplies exactly one. Emitting a
  // default and then overriding it puts two `border-*` classes on the element,
  // and which wins is decided by stylesheet order rather than class order.
  const base =
    'w-[20px] h-[20px] rounded-bg-v2-full shrink-0 flex items-center justify-center border-2';

  // In progress: the node's own border *is* the spinner — a light track with
  // one strong quadrant, turning.
  //
  // No glyph inside. A circular spinner drawn within a circular node is two
  // concentric rings in twenty pixels, and at that size they fight rather than
  // read as motion. One turning circle is unmistakable, and it leaves this as
  // the only node that is neither a solid fill nor a still ring — which is
  // what separates "working" from every settled state at a glance.
  //
  // The white fill still masks the connector, so the line appears to run
  // between the nodes rather than through them.
  if (meta.spinning) {
    return (
      <span
        title={meta.label}
        aria-label={meta.label}
        role="img"
        className={`${base} bg-bg-primary border-utility-blue-200 border-t-utility-blue-500 animate-spin`}
      />
    );
  }

  return (
    <span
      title={meta.label}
      aria-label={meta.label}
      role="img"
      className={`${base} ${
        nothingYet
          ? 'bg-bg-primary border-border-secondary'
          : `${node.fill} border-bg-primary`
      }`}
    >
      {!nothingYet && node.glyph && (
        <Icon name={node.glyph} size="xs" className="text-text-white" inheritColor />
      )}
    </span>
  );
};
/** The one pill an environment shows. */
export const EnvironmentBadge: React.FC<{
  status: EnvironmentStatus;
  runtime?: EnvironmentRuntime;
}> = ({ status, runtime }) => {
  const meta = environmentMeta(status, runtime);
  return (
    <Badge tone={meta.tone} spinning={meta.spinning}>
      {meta.label}
    </Badge>
  );
};

export default StatusBadge;
